<!--
  File name: FBNERCreate.vue
  Program Description:
    - This component contains the functions that communicate to BE for create, update, endorse, and approve
    - this.confirmDialog is triggered from FinalBreachEvaluationForm.vue
-->
<template>
  <v-container>
    <v-expansion-panels
      v-model="panels"
      flat
      multiple
      hover
      max="8"
    >
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Summary
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.organization"
                dense
                outlined
                disabled
                label="PIC"
                class="font-weight-bold text-body-1"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.dbnNumber"
                dense
                outlined
                disabled
                label="DBN Number"
                class="font-weight-bold text-body-1"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <DateComponent
                v-model="formItem.dateOfOccurrence"
                dense
                :disabled="disableField"
                :no-title="false"
                :value="formItem.dateOfOccurrence"
                :placeholder="''"
                :persistent-placeholder="false"
                outlined
                readonly
                whole-month
                label="Date of Occurrence"
                date-class="font-weight-bold text-body-1"
                open-picker-on-click
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="formItem.dateOfDiscovery"
                dense
                outlined
                disabled
                label="Date of Discovery"
                class="font-weight-bold text-body-1"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="formItem.dateOfNotification"
                dense
                outlined
                disabled
                label="Date of Notification of the commission"
                class="font-weight-bold text-body-1"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <TimePicker
                v-model="formItem.timeOfOccurrence"
                dense
                outlined
                :clearable="disableField"
                :disabled="disableField || !formItem.dateOfOccurrence"
                label="Time of Occurrence"
                class="font-weight-bold text-body-1"
              />
            </v-col>
            <v-col cols="4">
              <TimePicker
                v-model="formItem.timeOfDiscovery"
                dense
                outlined
                :clearable="disableField"
                :disabled="disableField"
                label="Time of Discovery"
                class="font-weight-bold text-body-1"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              class="pt-0"
              cols="4"
            >
              <h3
                class="gray--text font-weight-bold"
                style="font-size:18px"
              >
                Exemption to DS Notification
              </h3>
              <RadioButton
                v-model="formItem.exemptionToDSNotification"
                dense
                :can-edit="!disableField"
                :value="formItem.exemptionToDSNotification"
                :field-class="'font-weight-bold text-body-1 required-field'"
                :is-row="true"
                :options="exemptionOptions"
                :error-messages="genericError('formItem.exemptionToDSNotification', 'Exemption to DS Notification')"
                @input="$v.formItem.exemptionToDSNotification.$touch()"
                @blur="$v.formItem.exemptionToDSNotification.$touch()"
              />
            </v-col>
          </v-row>
          <v-row v-if="formItem.exemptionToDSNotification === 'Yes'">
            <v-col>
              <v-textarea
                v-model="formItem.justificationForExemption"
                dense
                label="Justification for exemption"
                rows="4"
                :disabled="disableField"
                :readonly="disableField"
                outlined
                class="font-weight-bold text-body-1 cursor"
                :error-messages="genericError('formItem.justificationForExemption', 'Justification for exemption')"
                @input="$v.formItem.justificationForExemption.$touch()"
                @blur="$v.formItem.justificationForExemption.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3
                class="gray--text font-weight-bold"
                style="font-size:18px"
              >
                Notified Data Subject
              </h3>
              <RadioButton
                v-model="formItem.notifiedDataSubject"
                :value="formItem.notifiedDataSubject"
                :can-edit="!disableField"
                :options="notifiedDataSubjOptions"
                field-class="font-weight-bold text-body-1"
                is-row
                dense
                @input="val => {
                  if (val === 'Yes') return
                  formItem.dateOfDSNotification = null
                  formItem.modeOfDSNotification = null
                  formItem.assistanceProvided = null
                }"
              />
            </v-col>
          </v-row>
          <v-row v-show="formItem.notifiedDataSubject === 'Yes' || isNotified">
            <v-col cols="4">
              <DateComponent
                v-model="formItem.dateOfDSNotification"
                dense
                :disabled="disableField"
                :no-title="false"
                :value="formItem.dateOfDSNotification"
                :placeholder="''"
                :persistent-placeholder="false"
                outlined
                readonly
                whole-month
                label="Date of DS Notification"
                date-class="font-weight-bold text-body-1 required-field"
                open-picker-on-click
                :error-messages="genericError('formItem.dateOfDSNotification', 'Date of DS Notification')"
                @input="$v.formItem.dateOfDSNotification.$touch()"
                @blur="$v.formItem.dateOfDSNotification.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.modeOfDSNotification"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Mode of DS Notification"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.modeOfDSNotification', 'Mode of DS Notification')"
                @input="$v.formItem.modeOfDSNotification.$touch()"
                @blur="$v.formItem.modeOfDSNotification.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.assistanceProvided"
                :disabled="disableField"
                :readonly="disableField"
                dense
                rows="4"
                outlined
                label="Assistance Provided to DS (if applicable)"
                class="font-weight-bold text-body-1 cursor"
              />
            </v-col>
          </v-row>
          <v-row v-show="formItem.notifiedDataSubject === 'No'">
            <v-col cols="4">
              <v-textarea
                v-model="formItem.reasonNotNotifiedDataSubject"
                class="font-weight-bold text-body-1 cursor"
                label="Reason for not notifying Data Subject"
                dense
                rows="4"
                outlined
                :disabled="disableField"
                :readonly="disableField"
                :error-messages="genericError('formItem.reasonNotNotifiedDataSubject', 'Reason for not notifying Data Subject')"
                @input="$v.formItem.reasonNotNotifiedDataSubject.$touch()"
                @blur="$v.formItem.reasonNotNotifiedDataSubject.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="formItem.dateOfSubmission"
                label="Date of submission of Final/Full Breach Report"
                class="font-weight-bold text-body-1"
                dense
                disabled
                outlined
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              class="pt-0"
              cols="4"
            >
              <h3
                class="gray--text font-weight-bold"
                style="font-size:18px"
              >
                Registration
              </h3>
              <RadioButton
                v-model="formItem.registration"
                dense
                :can-edit="!disableField"
                :value="formItem.registration"
                :field-class="'font-weight-bold text-body-1 required-field'"
                :is-row="true"
                :options="registrationOptions"
                :error-messages="genericError('formItem.registration', 'Registration')"
                @input="$v.formItem.registration.$touch()"
                @blur="$v.formItem.registration.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              class="pt-0"
              cols="4"
            >
              <h3
                class="gray--text font-weight-bold"
                style="font-size:18px"
              >
                Incident Involves Public Interests?
              </h3>
              <RadioButton
                v-model="formItem.isIncidentInvolve"
                dense
                :can-edit="!disableField"
                :value="formItem.isIncidentInvolve"
                :field-class="'font-weight-bold text-body-1 required-field'"
                :is-row="true"
                :options="isIncidentInvolveOptions"
                :error-messages="genericError('formItem.isIncidentInvolve', 'Incident Involves Public Interests')"
                @input="$v.formItem.isIncidentInvolve.$touch()"
                @blur="$v.formItem.isIncidentInvolve.$touch()"
              />
            </v-col>
          </v-row>
          <v-row v-show="formItem.isIncidentInvolve === 'Yes' || formItem.incidentInvolve && formItem.incidentInvolve.length > 0">
            <v-col cols="8">
              <LabelError
                error-message="Please select at least one (1) Incident involves public interest."
                :show-error="$v.formItem.incidentInvolve.$error"
                :bind="{
                  class: 'gray--text font-weight-bold',
                  style: 'font-size:19px'
                }"
              >
                Incident involves public interest
              </LabelError>
              <v-checkbox
                :id="`checkbox-incident-1`"
                v-model="formItem.incidentInvolve"
                multiple
                :disabled="disableField"
                class="font-weight-bold text-body-1 mt-3"
                label="The breach would likely affect national security, public safety, public order, or public health"
                value="The breach would likely affect national security, public safety, public order, or public health"
                dense
                @change="$v.formItem.incidentInvolve.$touch()"
              />
              <v-checkbox
                :id="`checkbox-incident-2`"
                v-model="formItem.incidentInvolve"
                multiple
                :disabled="disableField"
                class="font-weight-bold text-body-1 "
                label="It involves personal data of vulnerable groups or pose imminent danger to the same"
                value="It involves personal data of vulnerable groups or pose imminent danger to the same"
                dense
                @change="$v.formItem.incidentInvolve.$touch()"
              />
              <v-checkbox
                :id="`checkbox-incident-3`"
                v-model="formItem.incidentInvolve"
                multiple
                :disabled="disableField"
                class="font-weight-bold text-body-1"
                label="News of the data breach has been reported and circulated in tri-media or social media, and the breach will likely give rise to a real risk of serious harm and the breach will likely give rise to a real risk to the rights and freedoms of data subjects"
                value="News of the data breach has been reported and circulated in tri-media or social media, and the breach will likely give rise to a real risk of serious harm and the breach will likely give rise to a real risk to the rights and freedoms of data subjects"
                dense
                @change="$v.formItem.incidentInvolve.$touch()"
              />
              <v-checkbox
                :id="`checkbox-incident-4`"
                v-model="formItem.incidentInvolve"
                multiple
                :disabled="disableField"
                class="font-weight-bold text-body-1"
                label="Personal data of at least one hundred (100) individuals were affected"
                value="Personal data of at least one hundred (100) individuals were affected"
                dense
                @change="$v.formItem.incidentInvolve.$touch()"
              />
              <v-checkbox
                :id="`checkbox-incident-5`"
                v-model="formItem.incidentInvolve"
                multiple
                :disabled="disableField"
                class="font-weight-bold text-body-1"
                label="The Quick Response Report recommends the notification of the breach to the OPC"
                value="The Quick Response Report recommends the notification of the breach to the OPC"
                dense
                @change="$v.formItem.incidentInvolve.$touch()"
              />
            </v-col>
          </v-row>
          <h3
            class="gray--text font-weight-bold"
            style="font-size:18px"
          >
            Requests made by PIC/PIP
          </h3>
          <v-row v-if="formItem.pbnefRequest && formItem.pbnefRequest.length">
            <v-col>
              <v-list-item
                v-for="request in formItem.pbnefRequest"
                :key="request.pbnefId"
              >
                <v-list-item-icon>
                  <v-chip
                    :class="request.evaluation === 'Denied' ? 'red red--text': 'green green--text'"
                    class="text--darken-1 lighten-5"
                  >
                    {{ request.evaluation }}
                  </v-chip>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ request.typeOfRequest.join(',') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-list-item-content>
              <v-list-item>
                <v-list-item-title class="ml-3 font-weight-bold text-body-1">
                  None
                </v-list-item-title>
              </v-list-item>
            </v-list-item-content>
          </v-row>
          <v-row v-if="formItem.specifyIfOthers">
            <v-col>
              <v-text-field
                v-model="formItem.specifyIfOthers"
                dense
                outlined
                disabled
                append-icon=""
                label="Specify (if others)"
                class="font-weight-bold text-body-1"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.summary"
                dense
                label="Summary of the security incident"
                rows="4"
                :disabled="disableField"
                :readonly="disableField"
                outlined
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.summary', 'Summary of the security incident')"
                @input="$v.formItem.summary.$touch()"
                @blur="$v.formItem.summary.$touch()"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Findings
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Timeliness of the Report</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              class="py-0"
              cols="4"
            >
              <h3
                class="gray--text font-weight-bold"
                style="font-size:18px"
              >
                Notification Type
              </h3>
              <RadioButton
                v-model="formItem.typeOfNotification"
                dense
                :can-edit="!disableField"
                :value="formItem.typeOfNotification"
                :field-class="'font-weight-bold text-body-1 required-field'"
                :is-row="true"
                :options="notificationTypeOptions"
                :error-messages="genericError('formItem.typeOfNotification', 'Notification Type')"
                @input="$v.formItem.typeOfNotification.$touch()"
                @blur="$v.formItem.typeOfNotification.$touch()"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <VAutocomplete
                v-model="formItem.requisites"
                :disabled="disableField"
                :items="notifTypeItems"
                :error-messages="genericError('formItem.requisites', 'Requisites')"
                class="font-weight-bold text-body-1 required-field"
                label="Requisites"
                item-text="value"
                item-value="_id"
                multiple
                outlined
                chips
                @input="$v.formItem.requisites.$touch()"
                @blur="$v.formItem.requisites.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.timeliness"
                dense
                disabled
                label="Timeliness"
                outlined
                class="font-weight-bold text-body-1"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.justified"
                dense
                label="Justified, if delayed"
                disabled
                outlined
                class="font-weight-bold text-body-1"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.remarksTimeliness"
                dense
                label="Remarks"
                rows="4"
                :disabled="disableField"
                :readonly="disableField"
                outlined
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.remarksTimeliness', 'Remarks')"
                @input="$v.formItem.remarksTimeliness.$touch()"
                @blur="$v.formItem.remarksTimeliness.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Nature of the Incident</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <LookUpSelect
                v-model="formItem.generalCause"
                dense
                :label="'General Cause'"
                outlined
                class="font-weight-bold text-body-1"
                name="causeOfIncident"
                append-icon=""
                :allow-fetch="formItem.generalCause"
                :query="{'type': 'general'}"
                :item-text="'value'"
              />
            </v-col>
            <v-col cols="6">
              <LookUpSelect
                v-model="formItem.specificCause"
                dense
                :label="'Specific Cause'"
                outlined
                :additional-entries="[
                  {
                    _id: 'Others',
                    value: 'Others'
                  }
                ]"
                class="font-weight-bold text-body-1"
                name="causeOfIncident"
                append-icon=""
                :allow-fetch="formItem.generalCause"
                :query="{'type': 'specific'}"
                :item-text="'value'"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.remarksCause"
                dense
                rows="4"
                outlined
                label="Remarks"
                :disabled="disableField"
                :readonly="disableField"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.remarksCause', 'Remarks')"
                @input="$v.formItem.remarksCause.$touch()"
                @blur="$v.formItem.remarksCause.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.natureOfBreach"
                :items="natureOfBreach"
                class="font-weight-bold text-body-1 py-0 required-field"
                label="Nature of Breach"
                item-value="_id"
                item-text="value"
                deletable-chips
                multiple
                :disabled="disableField"
                outlined
                chips
                :error-messages="genericError('formItem.natureOfBreach', 'Nature of Breach')"
                @input="$v.formItem.natureOfBreach.$touch()"
                @blur="$v.formItem.natureOfBreach.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.numberOfAffectedDataRecords"
                dense
                outlined
                min="0"
                type="number"
                disabled
                label="Number of affected data subjects/records"
                class="font-weight-bold text-body-1 required-field"
                oninput="validity.valid||(value=&quot;&quot;)"
                :error-messages="genericError('formItem.numberOfAffectedDataRecords', 'Number of affected data subjects/records')"
                @input="$v.formItem.numberOfAffectedDataRecords.$touch()"
                @blur="$v.formItem.numberOfAffectedDataRecords.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-textarea
                v-model="formItem.sensitivePersonalInfo"
                dense
                disabled
                readonly
                label="Types of Sensitive Personal Information"
                rows="1"
                outlined
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.sensitivePersonalInfo', 'Sensitive Personal Information')"
                @input="$v.formItem.sensitivePersonalInfo.$touch()"
                @blur="$v.formItem.sensitivePersonalInfo.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.remarksBreach"
                dense
                rows="4"
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.remarksBreach', 'Remarks')"
                @input="$v.formItem.remarksBreach.$touch()"
                @blur="$v.formItem.remarksBreach.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.otherInfo"
                dense
                disabled
                readonly
                label="Types of other information that may enable identity fraud"
                rows="4"
                outlined
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.otherInfo', 'Types of other information that may enable identity fraud')"
                @input="$v.formItem.otherInfo.$touch()"
                @blur="$v.formItem.otherInfo.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.remarksOtherInfo"
                dense
                rows="4"
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.remarksOtherInfo', 'Remarks')"
                @input="$v.formItem.remarksOtherInfo.$touch()"
                @blur="$v.formItem.remarksOtherInfo.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.consequences"
                dense
                outlined
                disabled
                readonly
                label="Likely consequences as provided by the PIC/PIP"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.consequences', 'Likely consequences')"
                @input="$v.formItem.consequences.$touch()"
                @blur="$v.formItem.consequences.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.remarksConsequences"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.remarksConsequences', 'Remarks')"
                @input="$v.formItem.remarksConsequences.$touch()"
                @blur="$v.formItem.remarksConsequences.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="ml-n5 py-3">
              <h1 class="title primary--text">
                Compliance to the Data Privacy Act, its IRR, NPC Circular 16-03, and other issuances of the NPC
              </h1>
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Organizational Security Measures</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.organizationalFindings"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.organizationalFindings', 'Findings')"
                @input="$v.formItem.organizationalFindings.$touch()"
                @blur="$v.formItem.organizationalFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.organizationalReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.organizationalReferences', 'References')"
                @input="$v.formItem.organizationalReferences.$touch()"
                @blur="$v.formItem.organizationalReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.organizationalRecommendations"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.organizationalRecommendations', 'Remarks')"
                @input="$v.formItem.organizationalRecommendations.$touch()"
                @blur="$v.formItem.organizationalRecommendations.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Physical Security Measures</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.physicalFindings"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.physicalFindings', 'Findings')"
                @input="$v.formItem.physicalFindings.$touch()"
                @blur="$v.formItem.physicalFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.physicalReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.physicalReferences', 'References')"
                @input="$v.formItem.physicalReferences.$touch()"
                @blur="$v.formItem.physicalReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.physicalRecommendations"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.physicalRecommendations', 'Remarks')"
                @input="$v.formItem.physicalRecommendations.$touch()"
                @blur="$v.formItem.physicalRecommendations.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Technical Security Measures</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.technicalFindings"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.technicalFindings', 'Findings')"
                @input="$v.formItem.technicalFindings.$touch()"
                @blur="$v.formItem.technicalFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.technicalReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.technicalReferences', 'References')"
                @input="$v.formItem.technicalReferences.$touch()"
                @blur="$v.formItem.technicalReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.technicalRecommendations"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.technicalRecommendations', 'Remarks')"
                @input="$v.formItem.technicalRecommendations.$touch()"
                @blur="$v.formItem.technicalRecommendations.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Measures taken to address the breach</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.addressFindings"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.addressFindings', 'Findings')"
                @input="$v.formItem.addressFindings.$touch()"
                @blur="$v.formItem.addressFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.addressReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.addressReferences', 'References')"
                @input="$v.formItem.addressReferences.$touch()"
                @blur="$v.formItem.addressReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.addressRecommendations"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.addressRecommendations', 'Remarks')"
                @input="$v.formItem.addressRecommendations.$touch()"
                @blur="$v.formItem.addressRecommendations.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Measures to secure/recover personal data</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.secureFindings"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.secureFindings', 'Findings')"
                @input="$v.formItem.secureFindings.$touch()"
                @blur="$v.formItem.secureFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.secureReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.secureReferences', 'References')"
                @input="$v.formItem.secureReferences.$touch()"
                @blur="$v.formItem.secureReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.secureRecommendations"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.secureRecommendations', 'Remarks')"
                @input="$v.formItem.secureRecommendations.$touch()"
                @blur="$v.formItem.secureRecommendations.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Actions to mitigate harm</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.mitigateFindings"
                dense
                :disabled="disableField"
                :readonly="disableField"
                outlined
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.mitigateFindings', 'Findings')"
                @input="$v.formItem.mitigateFindings.$touch()"
                @blur="$v.formItem.mitigateFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.mitigateReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.mitigateReferences', 'References')"
                @input="$v.formItem.mitigateReferences.$touch()"
                @blur="$v.formItem.mitigateReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.mitigateRecommendations"
                dense
                :disabled="disableField"
                :readonly="disableField"
                outlined
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.mitigateRecommendations', 'Remarks')"
                @input="$v.formItem.mitigateRecommendations.$touch()"
                @blur="$v.formItem.mitigateRecommendations.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Actions to inform data subjects</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.informFindings"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.informFindings', 'Findings')"
                @input="$v.formItem.informFindings.$touch()"
                @blur="$v.formItem.informFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.informReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.informReferences', 'References')"
                @input="$v.formItem.informReferences.$touch()"
                @blur="$v.formItem.informReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.informRecommendations"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.informRecommendations', 'Remarks')"
                @input="$v.formItem.informRecommendations.$touch()"
                @blur="$v.formItem.informRecommendations.$touch()"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Measures to prevent recurrence of incidence</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.preventFindings"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Findings"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.preventFindings', 'Findings')"
                @input="$v.formItem.preventFindings.$touch()"
                @blur="$v.formItem.preventFindings.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.preventReferences"
                :items="formItem.reference"
                :disabled="disableField"
                multiple
                outlined
                chips
                label="References/Supporting Evidence"
                class="required-field chips-wrap"
                :error-messages="genericError('formItem.preventReferences', 'References')"
                @input="$v.formItem.preventReferences.$touch()"
                @blur="$v.formItem.preventReferences.$touch()"
              />
            </v-col>
            <v-col cols="4">
              <v-textarea
                v-model="formItem.preventRecommendations"
                dense
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                rows="4"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.preventRecommendations', 'Remarks')"
                @input="$v.formItem.preventRecommendations.$touch()"
                @blur="$v.formItem.preventRecommendations.$touch()"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Observations
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.summaryOfObservationsAndFindings"
                dense
                rows="4"
                label="Summary of observations and findings"
                outlined
                :disabled="disableField"
                :readonly="disableField"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.summaryOfObservationsAndFindings', 'Summary of observations and findings')"
                @input="$v.formItem.summaryOfObservationsAndFindings.$touch()"
                @blur="$v.formItem.summaryOfObservationsAndFindings.$touch()"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Recommendations
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-row>
            <v-col cols="4">
              <VAutocomplete
                v-model="formItem.recommendedAction"
                outlined
                :disabled="disableField"
                label="Recommended action"
                :items="[
                  'Issuance of Compliance Order',
                  'Investigation by CID',
                  'Closure',
                  'Compliance Check',
                  'For Administrative Fines'
                ]"
                class="font-weight-bold text-body-1 required-field"
                multiple
                clearable
                small-chips
                deletable-chips
                :error-messages="genericError('formItem.recommendedAction', 'Recommended action')"
                @input="$v.formItem.recommendedAction.$touch()"
                @blur="$v.formItem.recommendedAction.$touch()"
                @change="(val) => formItem.overallRecommendation = val"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.remarksRecommendation"
                dense
                rows="4"
                outlined
                :disabled="disableField"
                :readonly="disableField"
                label="Remarks"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.remarksRecommendation', 'Remarks')"
                @input="$v.formItem.remarksRecommendation.$touch()"
                @blur="$v.formItem.remarksRecommendation.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <LabelError
                error-message="Please select at least one (1) Possible violations of the DPA."
                :show-error="$v.formItem.possibleDpaViolation.$error"
                :bind="{
                  class: 'gray--text font-weight-bold',
                  style: 'font-size:19px'
                }"
              >
                Possible Violations of the DPA
              </LabelError>
              <v-checkbox
                :id="`checkbox-sec-25`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1 mt-3"
                label="SEC. 25. Unauthorized Processing of Personal Information and Sensitive Personal Information"
                value="SEC. 25. Unauthorized Processing of Personal Information and Sensitive Personal Information"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-sec-26`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1 "
                label="SEC. 26. Accessing Personal Information and Sensitive Personal Information Due to Negligence"
                value="SEC. 26. Accessing Personal Information and Sensitive Personal Information Due to Negligence"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-sec-27`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1"
                label="SEC. 27. Improper Disposal of Personal Information and Sensitive Personal Information"
                value="SEC. 27. Improper Disposal of Personal Information and Sensitive Personal Information"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-sec-28`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1"
                label="SEC. 28. Processing of Personal Information and Sensitive Personal Information for Unauthorized Purposes"
                value="SEC. 28. Processing of Personal Information and Sensitive Personal Information for Unauthorized Purposes"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-sec-29`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1"
                label="SEC. 29. Unauthorized Access or Intentional Breach"
                value="SEC. 29. Unauthorized Access or Intentional Breach"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-sec-30`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1"
                label="SEC. 30. Concealment of Security Breaches Involving Sensitive Personal Information"
                value="SEC. 30. Concealment of Security Breaches Involving Sensitive Personal Information"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-sec-31`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1"
                label="SEC. 31. Malicious Disclosure"
                value="SEC. 31. Malicious Disclosure"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-sec-32`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField || noPossibleDpaViolation"
                class="font-weight-bold text-body-1 "
                label="SEC. 32. Unauthorized Disclosure"
                value="SEC. 32. Unauthorized Disclosure"
                dense
                @click="$v.formItem.possibleDpaViolation.$touch()"
              />
              <v-checkbox
                :id="`checkbox-none`"
                v-model="formItem.possibleDpaViolation"
                multiple
                :disabled="disableField"
                class="font-weight-bold text-body-1 "
                label="None"
                value="None"
                dense
                @click="() => {
                  $v.formItem.possibleDpaViolation.$touch()
                  if (!noPossibleDpaViolation) return
                  formItem.possibleDpaViolation = ['None']
                }"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.remarksDpaViolation"
                dense
                label="Remarks"
                rows="4"
                outlined
                :disabled="disableField"
                :readonly="disableField"
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.remarksDpaViolation', 'Remarks')"
                @input="$v.formItem.remarksDpaViolation.$touch()"
                @blur="$v.formItem.remarksDpaViolation.$touch()"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Annex A (Contact Details of the PIC/PIP)
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.nameOfDpo"
                dense
                disabled
                outlined
                label="Name of DPO"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.nameOfDpo', 'Name of DPO')"
                @input="$v.formItem.nameOfDpo.$touch();"
                @blur="$v.formItem.nameOfDpo.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.officeAddress"
                dense
                :disabled="disableField"
                outlined
                label="Office Address"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.officeAddress', 'Office Address')"
                @input="$v.formItem.officeAddress.$touch();"
                @blur="$v.formItem.officeAddress.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.emailAddress"
                dense
                outlined
                disabled
                label="Email Address"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.emailAddress', 'Email Address')"
                @input="$v.formItem.emailAddress.$touch();"
                @blur="$v.formItem.emailAddress.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.mobileOrOfficeNumber"
                dense
                outlined
                disabled
                label="Mobile/Office Number"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.mobileOrOfficeNumber', 'Mobile/Office Number')"
                @input="$v.formItem.mobileOrOfficeNumber.$touch();"
                @blur="$v.formItem.mobileOrOfficeNumber.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.representativeName"
                dense
                outlined
                :disabled="disableField"
                label="Name of representative, if no appointed DPO"
                class="font-weight-bold text-body-1"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.representativeAddress"
                dense
                :disabled="disableField"
                outlined
                label="Office Address"
                class="font-weight-bold text-body-1"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.representativeEmail"
                dense
                outlined
                :disabled="disableField"
                label="Email Address"
                class="font-weight-bold text-body-1"
                :error-messages="genericError('formItem.representativeEmail', 'Email Address')"
                @input="$v.formItem.representativeEmail.$touch();"
                @blur="$v.formItem.representativeEmail.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formItem.representativeNumber"
                dense
                outlined
                :disabled="disableField"
                label="Mobile/Office Number"
                class="font-weight-bold text-body-1"
                :error-messages="genericError('formItem.representativeNumber', 'Mobile/Office Number')"
                @input="$v.formItem.representativeNumber.$touch();"
                @blur="$v.formItem.representativeNumber.$touch()"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Annex B (Compliance with Required Documents)
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-row>
            <v-col class="pb-0">
              <h3
                class="gray--text font-weight-bold"
                style="font-size:18px"
              >
                Show Cause Order
              </h3>
              <RadioButton
                :value="hasShowCauseOrder"
                :options="exemptionOptions"
                :can-edit="false"
                dense
                is-row
                hide-details
                field-class="font-weight-bold text-body-1"
              />
            </v-col>
          </v-row>
          <v-row v-show="hasShowCauseOrder === 'Yes'">
            <v-col>
              <v-textarea
                :value="formItem.showCauseReason || 'No Explanation.'"
                readonly
                outlined
                disabled
                dense
                rows="4"
                label="Explanation for non-submission of required documents (compliance with show cause order)"
                class="font-weight-bold text-body-1 cursor"
              />
            </v-col>
          </v-row>
          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Status of compliance</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="2"
              class="pt-0"
            >
              <v-col class="px-0 pt-0 mb-3">
                <h3
                  class="gray--text font-weight-bold"
                  style="font-size:18px"
                >
                  Documents required
                </h3>
              </v-col>

              <h3 class="black--text font-weight-bold text-body-1">
                Initial Report
              </h3>
            </v-col>

            <v-col
              cols="3"
              class="pt-0"
            >
              <v-col class="px-0 pt-0 mb-4">
                <h3
                  class="gray--text font-weight-bold"
                  style="font-size:18px"
                >
                  Legal Basis for Requirement
                </h3>
              </v-col>
              <v-row>
                <v-text-field
                  :value="'Section 17, NPC Circular 2016-03'"
                  dense
                  disabled
                  solo
                  flat
                  class="font-weight-bold text-body-1"
                />
              </v-row>
            </v-col>
            <v-col
              cols="4"
              class="mt-5"
            >
              <VAutocomplete
                v-model="formItem.initialStatusOfCompliance"
                dense
                label="Status of compliance"
                outlined
                :disabled="disableField"
                :items="[
                  'Not Compliant',
                  'Incomplete but not substantially compliant',
                  'Incomplete but substantially compliant',
                  'Fully Compliant'
                ]"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.initialStatusOfCompliance', 'Status of compliance')"
                @input="$v.formItem.initialStatusOfCompliance.$touch()"
                @blur="$v.formItem.initialStatusOfCompliance.$touch()"
              />
            </v-col>
            <v-col
              cols="3"
              class="mt-5"
            >
              <DateComponent
                v-model="formItem.initialDateOfCompliance"
                dense
                whole-month
                :placeholder="''"
                :disabled="disableField"
                :no-title="false"
                :value="formItem.initialDateOfCompliance"
                :persistent-placeholder="false"
                readonly
                outlined
                open-picker-on-click
                label="Date of compliance"
                date-class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.initialDateOfCompliance', 'Date of compliance')"
                @input="$v.formItem.initialDateOfCompliance.$touch()"
                @blur="$v.formItem.initialDateOfCompliance.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="2">
              <h3 class="black--text font-weight-bold text-body-1">
                Full Report
              </h3>
            </v-col>

            <v-col
              cols="3"
              class="mt-1"
            >
              <v-row>
                <v-text-field
                  :value="'Section 17, NPC Circular 2016-03'"
                  dense
                  disabled
                  solo
                  flat
                  class="font-weight-bold text-body-1"
                />
              </v-row>
            </v-col>

            <v-col
              cols="4"
              class="pt-0"
            >
              <VAutocomplete
                v-model="formItem.fullStatusOfCompliance"
                dense
                label="Status of compliance"
                outlined
                :disabled="disableField"
                :items="[
                  'Not Compliant',
                  'Incomplete but not substantially compliant',
                  'Incomplete but substantially compliant',
                  'Fully Compliant'
                ]"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.fullStatusOfCompliance', 'Status of compliance')"
                @input="$v.formItem.fullStatusOfCompliance.$touch()"
                @blur="$v.formItem.fullStatusOfCompliance.$touch()"
              />
            </v-col>
            <v-col
              cols="3"
              class="pt-0"
            >
              <DateComponent
                v-model="formItem.fullDateOfCompliance"
                dense
                :disabled="disableField"
                :placeholder="''"
                :no-title="false"
                whole-month
                :value="formItem.fullDateOfCompliance"
                :persistent-placeholder="false"
                readonly
                outlined
                open-picker-on-click
                label="Date of compliance"
                date-class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.fullDateOfCompliance', 'Date of compliance')"
                @input="$v.formItem.fullDateOfCompliance.$touch()"
                @blur="$v.formItem.fullDateOfCompliance.$touch()"
              />
            </v-col>
          </v-row>

          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Documents Required</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <VAutocomplete
                v-model="formItem.typeOfIssuance"
                dense
                outlined
                :disabled="disableField"
                :items="['Order', 'Decision', 'Resolution']"
                label="Type of Issuance"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.typeOfIssuance', 'Type of Issuance')"
                @input="$v.formItem.typeOfIssuance.$touch()"
                @blur="$v.formItem.typeOfIssuance.$touch()"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                :value="formItem.dateOfIssuance"
                dense
                disabled
                readonly
                outlined
                label="Date of Issuance"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.dateOfIssuance', 'Date of Issuance')"
              />
            </v-col>
          </v-row>
          <template v-if="formItem.docsArray.length > 0">
            <template v-for="(docRequired, idx) in formItem.docsArray">
              <v-row :key="`documents-row-top-${idx}`">
                <v-col cols="6">
                  <v-text-field
                    v-model="formItem.docsArray[idx].document"
                    dense
                    outlined
                    :disabled="disableField || formItem.docsArray[idx].disabled"
                    :readonly="disableField || formItem.docsArray[idx].disabled"
                    label="Document"
                    class="font-weight-bold text-body-1 required-field"
                    :error-messages="genericErrorObjectArrayFormItem('docsArray', idx, 'document', 'Document')"
                    @input="$v.formItem.docsArray.$each[idx].document.$touch()"
                    @blur="$v.formItem.docsArray.$each[idx].document.$touch()"
                  />
                </v-col>
                <v-col cols="6">
                  <VAutocomplete
                    v-model="formItem.docsArray[idx].legalBasisForRequirement"
                    outlined
                    :disabled="disableField"
                    :items="legalBasisOptions"
                    multiple
                    clearable
                    small-chips
                    deletable-chips
                    label="Legal Basis for Requirement"
                    class="font-weight-bold text-body-1"
                  />
                </v-col>
              </v-row>
              <v-row :key="`documents-row-bottom-${idx}`">
                <v-col cols="6">
                  <VAutocomplete
                    v-model="formItem.docsArray[idx].statusOfCompliance"
                    dense
                    :disabled="disableField"
                    :items="[
                      'Not Compliant',
                      'Incomplete but not substantially compliant',
                      'Incomplete but substantially compliant',
                      'Fully Compliant',
                    ]"
                    outlined
                    label="Status of compliance"
                    class="font-weight-bold text-body-1"
                  />
                </v-col>
                <v-col cols="6">
                  <DateComponent
                    v-model="formItem.docsArray[idx].dateOfCompliance"
                    dense
                    :disabled="disableField"
                    :placeholder="''"
                    :no-title="false"
                    :persistent-placeholder="false"
                    whole-month
                    outlined
                    readonly
                    clearable
                    open-picker-on-click
                    :value="formItem.docsArray[idx].dateOfCompliance"
                    label="Date of compliance"
                    date-class="font-weight-bold text-body-1"
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="idx === formItem.docsArray.length - 1 && !disableField"
                :key="`add-document-row-${idx}`"
              >
                <v-col class="pt-0 mt-n4">
                  <v-btn
                    text
                    class="pa-2 primary--text text-caption font-weight-bold"
                    @click="addDocRow({ disabled: false })"
                  >
                    <span class="text-subtitle-1">Add another document</span>
                    <v-icon class="ml-1 primary--text">
                      mdi-plus-circle
                    </v-icon>
                  </v-btn>
                  <v-btn
                    v-if="formItem.docsArray.length > 1"
                    icon
                    class="pa-0 ml-2 primary--text font-weight-bold"
                    @click="formItem.docsArray.pop()"
                  >
                    <v-icon class="ml-1 red--text">
                      mdi-minus-circle
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </template>

          <v-row class="pb-2">
            <v-col class="primary--text">
              <h2>Recommendation</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-combobox
                v-model="formItem.overallRecommendation"
                dense
                disabled
                append-icon=""
                outlined
                label="Recommendation"
                class="font-weight-bold text-body-1 required-field"
                multiple
                chips
                small-chips
                :error-messages="genericError('formItem.overallRecommendation', 'Recommendation')"
                @input="$v.formItem.overallRecommendation.$touch()"
                @blur="$v.formItem.overallRecommendation.$touch()"
              >
                <template v-slot:selection="{ item }">
                  <v-chip
                    class="mt-2"
                    small
                  >
                    {{ item }}
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="6">
              <VAutocomplete
                v-model="formItem.overall"
                dense
                outlined
                :disabled="disableField"
                :label="'Over all level of compliance'"
                :items=" [
                  'Not Compliant',
                  'Incomplete but not substantially compliant',
                  'Incomplete but substantially compliant',
                  'Fully Compliant'
                ]"
                class="font-weight-bold text-body-1 required-field"
                :error-messages="genericError('formItem.overall', 'Over all level of compliance')"
                @input="$v.formItem.overall.$touch()"
                @blur="$v.formItem.overall.$touch()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="formItem.overallRemarks"
                dense
                label="Remarks"
                rows="4"
                :disabled="disableField"
                :readonly="disableField"
                outlined
                class="font-weight-bold text-body-1 required-field cursor"
                :error-messages="genericError('formItem.overallRemarks', 'Remarks')"
                @input="$v.formItem.overallRemarks.$touch()"
                @blur="$v.formItem.overallRemarks.$touch()"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Annex C (Elements of Violations under the Data Privacy Act)
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-row>
            <v-col class="primary--text pb-0">
              <h2>Violations</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-25`"
                v-model="formItem.violations"
                multiple
                :disabled="disableField"
                class="font-weight-bold text-body-1 mt-3"
                label="SEC. 25. Unauthorized Processing of Personal Information and Sensitive Personal Information"
                value="SEC. 25. Unauthorized Processing of Personal Information and Sensitive Personal Information"
                dense
                :error-messages="errorMessages({field: 'specifyIfSec25', message: 'Please select at least one (1) SEC. 25 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 25. Unauthorized Processing of Personal Information and Sensitive Personal Information')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-1`"
                    v-model="formItem.specifyIfSec25"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="There is personal information (PI)"
                    value="There is personal information (PI)"
                    dense
                    @click="$v.formItem.specifyIfSec25.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-2`"
                    v-model="formItem.specifyIfSec25"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is sensitive personal information (SPI)"
                    value="There is sensitive personal information (SPI)"
                    dense
                    @click="$v.formItem.specifyIfSec25.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-3`"
                    v-model="formItem.specifyIfSec25"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is processing of PI/SPI"
                    value="There is processing of PI/SPI"
                    dense
                    @click="$v.formItem.specifyIfSec25.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-4`"
                    v-model="formItem.specifyIfSec25"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The processing of PI/SPI was without the consent of the data subject, or not authorized under the DPA or any existing law"
                    value="The processing of PI/SPI was without the consent of the data subject, or not authorized under the DPA or any existing law"
                    dense
                    @click="$v.formItem.specifyIfSec25.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-26`"
                v-model="formItem.violations"
                :disabled="disableField"
                class="font-weight-bold text-body-1 "
                label="SEC. 26. Accessing Personal Information and Sensitive Personal Information Due to Negligence"
                value="SEC. 26. Accessing Personal Information and Sensitive Personal Information Due to Negligence"
                dense
                :error-messages="errorMessages({field: 'specifyIfSec26', message: 'Please select at least one (1) SEC. 26 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 26. Accessing Personal Information and Sensitive Personal Information Due to Negligence')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-5`"
                    v-model="formItem.specifyIfSec26"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is personal information (PI)"
                    value="There is personal information (PI)"
                    dense
                    @click="$v.formItem.specifyIfSec26.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-6`"
                    v-model="formItem.specifyIfSec26"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is sensitive personal information (SPI)"
                    value="There is sensitive personal information (SPI)"
                    dense
                    @click="$v.formItem.specifyIfSec26.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-7`"
                    v-model="formItem.specifyIfSec26"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The PI/SPI was accessed"
                    value="The PI/SPI was accessed"
                    dense
                    @click="$v.formItem.specifyIfSec26.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-8`"
                    v-model="formItem.specifyIfSec26"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The access was not authorized under the DPA or any existing law"
                    value="The access was not authorized under the DPA or any existing law"
                    dense
                    @click="$v.formItem.specifyIfSec26.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-9`"
                    v-model="formItem.specifyIfSec26"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The unauthorized access was due to the negligence of any person"
                    value="The unauthorized access was due to the negligence of any person"
                    dense
                    @click="$v.formItem.specifyIfSec26.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-27`"
                v-model="formItem.violations"
                class="font-weight-bold text-body-1 "
                :disabled="disableField"
                label="SEC. 27. Improper Disposal of Personal Information and Sensitive Personal Information"
                value="SEC. 27. Improper Disposal of Personal Information and Sensitive Personal Information"
                dense
                :error-messages="errorMessages({field: 'specifyIfSec27', message: 'Please select at least one (1) SEC. 27 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 27. Improper Disposal of Personal Information and Sensitive Personal Information')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-10`"
                    v-model="formItem.specifyIfSec27"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is personal information (PI)"
                    value="There is personal information (PI)"
                    dense
                    @click="$v.formItem.specifyIfSec27.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-11`"
                    v-model="formItem.specifyIfSec27"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is sensitive personal information (SPI)"
                    value="There is sensitive personal information (SPI)"
                    dense
                    @click="$v.formItem.specifyIfSec27.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-12`"
                    v-model="formItem.specifyIfSec27"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The PI/SPI was disposed, discarded, or abandoned"
                    value="The PI/SPI was disposed, discarded, or abandoned"
                    dense
                    @click="$v.formItem.specifyIfSec27.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-13`"
                    v-model="formItem.specifyIfSec27"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The place of disposal is an area accessible to the public or the PI/SPI was placed in its container for trash collection"
                    value="The place of disposal is an area accessible to the public or the PI/SPI was placed in its container for trash collection"
                    dense
                    @click="$v.formItem.specifyIfSec27.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-28`"
                v-model="formItem.violations"
                :disabled="disableField"
                class="font-weight-bold text-body-1 "
                label="SEC. 28. Processing of Personal Information and Sensitive Personal Information for Unauthorized Purposes"
                value="SEC. 28. Processing of Personal Information and Sensitive Personal Information for Unauthorized Purposes"
                dense
                :error-messages="errorMessages({field: 'specifyIfSec28', message: 'Please select at least one (1) SEC. 28 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 28. Processing of Personal Information and Sensitive Personal Information for Unauthorized Purposes')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-14`"
                    v-model="formItem.specifyIfSec28"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is personal information (PI)"
                    value="There is personal information (PI)"
                    dense
                    @click="$v.formItem.specifyIfSec28.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-15`"
                    v-model="formItem.specifyIfSec28"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is sensitive personal information (SPI)"
                    value="There is sensitive personal information (SPI)"
                    dense
                    @click="$v.formItem.specifyIfSec28.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-16`"
                    v-model="formItem.specifyIfSec28"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="There is processing of PI/SPI"
                    value="There is processing of PI/SPI"
                    dense
                    @click="$v.formItem.specifyIfSec28.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-17`"
                    v-model="formItem.specifyIfSec28"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The person processing the PI/SPI was initially authorized to process the same"
                    value="The person processing the PI/SPI was initially authorized to process the same"
                    dense
                    @click="$v.formItem.specifyIfSec28.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-18`"
                    v-model="formItem.specifyIfSec28"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The processing is for a purpose not authorized by the data subject, under this Act or under existing laws"
                    value="The processing is for a purpose not authorized by the data subject, under this Act or under existing laws"
                    dense
                    @click="$v.formItem.specifyIfSec28.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-29`"
                v-model="formItem.violations"
                :disabled="disableField"
                class="font-weight-bold text-body-1 "
                label="SEC. 29. Unauthorized Access or Intentional Breach"
                value="SEC. 29. Unauthorized Access or Intentional Breach"
                dense
                :error-messages="errorMessages({field: 'specifyIfSec29', message: 'Please select at least one (1) SEC. 29 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 29. Unauthorized Access or Intentional Breach')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-19`"
                    v-model="formItem.specifyIfSec29"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="There is a system where personal and sensitive personal information is stored"
                    value="There is a system where personal and sensitive personal information is stored"
                    dense
                    @click="$v.formItem.specifyIfSec29.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-20`"
                    v-model="formItem.specifyIfSec29"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="A person breaks into the system"
                    value="A person breaks into the system"
                    dense
                    @click="$v.formItem.specifyIfSec29.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-21`"
                    v-model="formItem.specifyIfSec29"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The breaking of the system was done knowingly and unlawfully, or violating data confidentiality and security data systems"
                    value="The breaking of the system was done knowingly and unlawfully, or violating data confidentiality and security data systems"
                    dense
                    @click="$v.formItem.specifyIfSec29.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-30`"
                v-model="formItem.violations"
                class="font-weight-bold text-body-1 "
                label="SEC. 30. Concealment of Security Breaches Involving Sensitive Personal Information"
                value="SEC. 30. Concealment of Security Breaches Involving Sensitive Personal Information"
                dense
                :disabled="disableField"
                :error-messages="errorMessages({field: 'specifyIfSec30', message: 'Please select at least one (1) SEC. 30 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 30. Concealment of Security Breaches Involving Sensitive Personal Information')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-22`"
                    v-model="formItem.specifyIfSec30"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="A personal data breach occurred"
                    value="A personal data breach occurred"
                    dense
                    @click="$v.formItem.specifyIfSec30.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-23`"
                    v-model="formItem.specifyIfSec30"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The breach is required to be notified to the NPC"
                    value="The breach is required to be notified to the NPC"
                    dense
                    @click="$v.formItem.specifyIfSec30.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-24`"
                    v-model="formItem.specifyIfSec30"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The person knows of the breach and the obligation to notify the NPC"
                    value="The person knows of the breach and the obligation to notify the NPC"
                    dense
                    @click="$v.formItem.specifyIfSec30.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-25`"
                    v-model="formItem.specifyIfSec30"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The fact of such breach was not notified to the NPC"
                    value="The fact of such breach was not notified to the NPC"
                    dense
                    @click="$v.formItem.specifyIfSec30.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-31`"
                v-model="formItem.violations"
                :disabled="disableField"
                class="font-weight-bold text-body-1 "
                label="SEC. 31. Malicious Disclosure"
                value="SEC. 31. Malicious Disclosure"
                dense
                :error-messages="errorMessages({field: 'specifyIfSec31', message: 'Please select at least one (1) SEC. 31 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 31. Malicious Disclosure')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-26`"
                    v-model="formItem.specifyIfSec31"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The offender is a personal information controller or personal information processor or any of its officials, employees or agents"
                    value="The offender is a personal information controller or personal information processor or any of its officials, employees or agents"
                    dense
                    @click="$v.formItem.specifyIfSec31.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-27`"
                    v-model="formItem.specifyIfSec31"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The offender disclosed unwarranted or false information"
                    value="The offender disclosed unwarranted or false information"
                    dense
                    @click="$v.formItem.specifyIfSec31.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-28`"
                    v-model="formItem.specifyIfSec31"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The unwarranted or false information refers to any personal information (PI) or sensitive personal information (SPI)"
                    value="The unwarranted or false information refers to any personal information (PI) or sensitive personal information (SPI)"
                    dense
                    @click="$v.formItem.specifyIfSec31.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-29`"
                    v-model="formItem.specifyIfSec31"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The PI/SPI was obtained by him/her"
                    value="The PI/SPI was obtained by him/her"
                    dense
                    @click="$v.formItem.specifyIfSec31.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-30`"
                    v-model="formItem.specifyIfSec31"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The disclosure was attended by malice or bad faith"
                    value="The disclosure was attended by malice or bad faith"
                    dense
                    @click="$v.formItem.specifyIfSec31.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                :id="`checkbox-sec-32`"
                v-model="formItem.violations"
                :disabled="disableField"
                class="font-weight-bold text-body-1 "
                label="SEC. 32. Unauthorized Disclosure"
                value="SEC. 32. Unauthorized Disclosure"
                dense
                :error-messages="errorMessages({field: 'specifyIfSec32', message: 'Please select at least one (1) SEC. 32 subdata.'})"
              />
              <template v-if="formItem.violations.includes('SEC. 32. Unauthorized Disclosure')">
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-31`"
                    v-model="formItem.specifyIfSec32"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The offender is a personal information controller or personal information processor or any of its officials, employees or agents"
                    value="The offender is a personal information controller or personal information processor or any of its officials, employees or agents"
                    dense
                    @click="$v.formItem.specifyIfSec32.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-32`"
                    v-model="formItem.specifyIfSec32"
                    class="font-weight-bold  "
                    multiple
                    :disabled="disableField"
                    label="The information refers to any personal information (PI) or sensitive personal information (SPI)"
                    value="The information refers to any personal information (PI) or sensitive personal information (SPI)"
                    dense
                    @click="$v.formItem.specifyIfSec32.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-33`"
                    v-model="formItem.specifyIfSec32"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The information is not unwarranted or false"
                    value="The information is not unwarranted or false"
                    dense
                    @click="$v.formItem.specifyIfSec32.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-33`"
                    v-model="formItem.specifyIfSec32"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The PI/SPI was obtained by him/her"
                    value="The PI/SPI was obtained by him/her"
                    dense
                    @click="$v.formItem.specifyIfSec32.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-34`"
                    v-model="formItem.specifyIfSec32"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The disclosure was without the consent of the data subject"
                    value="The disclosure was without the consent of the data subject"
                    dense
                    @click="$v.formItem.specifyIfSec32.$touch()"
                  />
                </v-col>
                <v-col
                  class="py-0"
                >
                  <v-checkbox
                    :id="`subcheckbox-35`"
                    v-model="formItem.specifyIfSec32"
                    multiple
                    :disabled="disableField"
                    class="font-weight-bold  "
                    label="The disclosure was not attended by malice or bad faith"
                    value="The disclosure was not attended by malice or bad faith"
                    dense
                    @click="$v.formItem.specifyIfSec32.$touch()"
                  />
                </v-col>
              </template>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0">
          <h1 class="title primary--text">
            Annex D (Timeline of Actions Taken by the PIC/PIP and CMD)
          </h1>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <template v-for="(action, idx) in formItem.actionTakenArray">
            <v-row :key="`actionTaken-row-bottom-${idx}`">
              <v-col>
                <v-text-field
                  v-model="formItem.actionTakenArray[idx].actionTaken"
                  dense
                  :disabled="disableField"
                  outlined
                  label="Action Taken"
                  class="font-weight-bold text-body-1 required-field"
                  :error-messages="genericErrorObjectArrayFormItem('actionTakenArray', idx, 'actionTaken', 'Action Taken')"
                  @input="$v.formItem.actionTakenArray.$each[idx].actionTaken.$touch()"
                  @blur="$v.formItem.actionTakenArray.$each[idx].actionTaken.$touch()"
                />
              </v-col>
              <v-col>
                <DateComponent
                  v-model="formItem.actionTakenArray[idx].date"
                  :value="formItem.actionTakenArray[idx].date"
                  dense
                  :disabled="disableField"
                  :placeholder="''"
                  :no-title="false"
                  :persistent-placeholder="false"
                  open-picker-on-click
                  whole-month
                  readonly
                  outlined
                  label="Date"
                  date-class="font-weight-bold text-body-1 required-field"
                  :error-messages="genericErrorObjectArrayFormItem('actionTakenArray', idx, 'date', 'Date')"
                  @input="$v.formItem.actionTakenArray.$each[idx].date.$touch()"
                  @blur="$v.formItem.actionTakenArray.$each[idx].date.$touch()"
                />
              </v-col>
              <v-col>
                <VAutocomplete
                  v-model="formItem.actionTakenArray[idx].timeliness"
                  dense
                  outlined
                  :disabled="disableField"
                  :items="[
                    'Timely',
                    'Delayed',
                    'Not Applicable'
                  ]"
                  label="Timeliness of compliance"
                  class="font-weight-bold text-body-1 required-field"
                  :error-messages="genericErrorObjectArrayFormItem('actionTakenArray', idx, 'timeliness', 'Timeliness')"
                  @input="$v.formItem.actionTakenArray.$each[idx].timeliness.$touch()"
                  @blur="$v.formItem.actionTakenArray.$each[idx].timeliness.$touch()"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="idx === formItem.actionTakenArray.length - 1 && !disableField"
              :key="`add-actionTaken-row-${idx}`"
            >
              <v-col class="pt-0 mt-n4">
                <v-btn
                  text
                  class="pa-2 primary--text text-caption font-weight-bold"
                  @click="addActionTakenRow()"
                >
                  <span class="text-subtitle-1">Add another Action Taken</span>
                  <v-icon class="ml-1 primary--text">
                    mdi-plus-circle
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="formItem.actionTakenArray.length > 1"
                  icon
                  class="pa-0 ml-2 primary--text font-weight-bold"
                  @click="formItem.actionTakenArray.pop()"
                >
                  <v-icon class="ml-1 red--text">
                    mdi-minus-circle
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
          <!-- <v-row>
      <v-col class="ml-n5 pb-10 pt-4">
        <h1 class="title primary--text">
          Signatories
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="4">
        <span class="pl-3 gray--text font-weight-bold">Evaluated by</span>
        <v-text-field
          :value="formItem.evaluatedBy && formItem.evaluatedBy.fullName || `-`"
          dense
          disabled
          solo
          flat
          class="font-weight-bold text-body-1"
        />
      </v-col>
      <v-col
        cols="4"
      >
        <span class="pl-3 gray--text font-weight-bold">Reviewed by</span>
        <v-text-field
          :value="(formItem.reviewedBy && formItem.reviewedBy.fullName) || `-`"
          dense
          disabled
          solo
          flat
          class="font-weight-bold text-body-1"
        />
      </v-col>
      <v-col cols="4">
        <span class="pl-3 gray--text font-weight-bold">Approved by</span>
        <v-text-field
          :value="(formItem.approvedBy && formItem.approvedBy.fullName) || `-`"
          dense
          disabled
          solo
          flat
          class="font-weight-bold text-body-1"
        />
      </v-col>
    </v-row> -->
          <template v-if="enableUpload">
            <v-row
              v-if="canViewUploadHeader"
              class="pb-2"
            >
              <v-col class="primary--text">
                <h2>File Upload</h2>
              </v-col>
            </v-row>
            <v-row style="gap: 3rem">
              <v-col
                cols="3"
              >
                <template v-if="viewFirstUpload">
                  <v-row>
                    <p class="gray--text font-weight-bold ma-auto mb-3">
                      {{ firstUploadField }}
                    </p>
                  </v-row>
                  <template v-if="firstUploadField === 'Adjudication'">
                    <v-row>
                      <v-col>
                        <v-file-input
                          v-model="formItem.adjudicationFileUpload"
                          prepend-icon="mdi-file"
                          color="primary"
                          outlined
                          :disabled="!assignedAdjudicator"
                          accept=".pdf"
                          :error-messages="genericError('formItem.adjudicationFileUpload', 'File upload')"
                          @change="$v.formItem.adjudicationFileUpload.$touch()"
                        >
                          <template v-slot:selection="{ text, file }">
                            {{ text || file.originalname }}
                          </template>
                        </v-file-input>
                      </v-col>
                      <v-btn
                        icon
                        color="primary"
                        class="mt-4"
                        @click="download(`attachment/fbner/${$route.params.id}`, formItem.adjudicationFileUpload)"
                      >
                        <v-icon size="28">
                          mdi-download
                        </v-icon>
                      </v-btn>
                    </v-row>
                  </template>
                  <template v-else>
                    <v-row>
                      <v-col>
                        <v-file-input
                          v-model="formItem.investigationFileUpload"
                          prepend-icon="mdi-file"
                          color="primary"
                          outlined
                          :disabled="!assignedInvestigator"
                          accept=".pdf"
                          :error-messages="genericError('formItem.investigationFileUpload', 'File upload')"
                          @change="$v.formItem.investigationFileUpload.$touch()"
                        >
                          <template v-slot:selection="{ text, file }">
                            {{ text || file.originalname }}
                          </template>
                        </v-file-input>
                      </v-col>
                      <v-btn
                        icon
                        color="primary"
                        class="mt-4"
                        @click="download(`attachment/fbner/${$route.params.id}`, formItem.investigationFileUpload)"
                      >
                        <v-icon size="28">
                          mdi-download
                        </v-icon>
                      </v-btn>
                    </v-row>
                  </template>
                </template>
              </v-col>
              <v-col
                cols="3"
              >
                <template
                  v-if="currentStatus.value === 'For Enforcement' ||
                    (formItem.investigationFileUpload.size && currentStatus.value === 'For Adjudication')"
                >
                  <template v-if="viewSecondUpload">
                    <v-row>
                      <p class="gray--text font-weight-bold ma-auto mb-3">
                        {{ secondUploadField }}
                      </p>
                    </v-row>
                    <template v-if="secondUploadField === 'Adjudication'">
                      <v-row>
                        <v-col>
                          <v-file-input
                            v-model="formItem.adjudicationFileUpload "
                            prepend-icon="mdi-file"
                            color="primary"
                            outlined
                            accept=".pdf"
                            :disabled="!assignedAdjudicator "
                            :error-messages="genericError('formItem.adjudicationFileUpload', 'File upload')"
                            @change="$v.formItem.adjudicationFileUpload.$touch() "
                          >
                            <template v-slot:selection="{ text, file }">
                              {{ text || file.originalname }}
                            </template>
                          </v-file-input>
                        </v-col>

                        <v-btn
                          icon
                          color="primary"
                          class="mt-4"
                          @click="download(`attachment/fbner/${$route.params.id}`,formItem.adjudicationFileUpload)"
                        >
                          <v-icon size="28">
                            mdi-download
                          </v-icon>
                        </v-btn>
                      </v-row>
                    </template>
                    <template v-else>
                      <v-row>
                        <v-col>
                          <v-file-input
                            v-model="formItem.enforcementFileUpload"
                            prepend-icon="mdi-file"
                            color="primary"
                            outlined
                            accept=".pdf"
                            :disabled="!assignedEnforcer "
                            :error-messages="genericError('formItem.enforcementFileUpload', 'File upload')"
                            @change="$v.formItem.enforcementFileUpload.$touch() "
                          >
                            <template v-slot:selection="{ text, file }">
                              {{ text || file.originalname }}
                            </template>
                          </v-file-input>
                        </v-col>

                        <v-btn
                          icon
                          color="primary"
                          class="mt-4"
                          @click="download(`attachment/fbner/${$route.params.id}`,formItem.enforcementFileUpload)"
                        >
                          <v-icon size="28">
                            mdi-download
                          </v-icon>
                        </v-btn>
                      </v-row>
                    </template>
                  </template>
                </template>
              </v-col>
              <v-col
                cols="3"
              >
                <template v-if="secondUploadField === 'Adjudication' && currentStatus.value === 'For Enforcement'">
                  <template v-if="viewThirdUpload">
                    <v-row>
                      <p class="gray--text font-weight-bold ma-auto mb-3">
                        Enforcement
                      </p>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-file-input
                          v-model="formItem.enforcementFileUpload"
                          prepend-icon="mdi-file"
                          color="primary"
                          outlined
                          accept=".pdf"
                          :disabled="!assignedEnforcer"
                          :error-messages="genericError('formItem.enforcementFileUpload', 'File upload')"
                          @change="$v.formItem.enforcementFileUpload.$touch()"
                        >
                          <template v-slot:selection="{ text, file }">
                            {{ text || file.originalname }}
                          </template>
                        </v-file-input>
                      </v-col>

                      <v-btn
                        icon
                        color="primary"
                        class="mt-4"
                        @click="download(`attachment/fbner/${$route.params.id}`, formItem.enforcementFileUpload)"
                      >
                        <v-icon size="28">
                          mdi-download
                        </v-icon>
                      </v-btn>
                    </v-row>
                  </template>
                </template>
              </v-col>
            </v-row>
          </template>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
import fbnerMixin from '@/mixins/fbnerMixin'
import crud from '@/utils/rest/crud'
import fbner from '@/utils/webapi/fbner'

import { validationMixin, validationErrors, custom } from '@/utils/validations'
export default {
  name: 'FBNERCreate',
  mixins: [
    fbnerMixin, validationMixin
  ],
  data: () => ({
    status: '',
    panels: [0, 1, 2, 3, 4, 5, 6, 7]
  }),
  computed: {
    ...validationErrors,
    defaultView () {
      return this.roleView === 'default'
    },
    disableField () {
      const unAllowed = ['For Adjudication', 'For Investigation', 'For Enforcement', 'Closed']
      return unAllowed.includes(this.currentStatus.value)
    },
    assignedAdjudicator () {
      const adjudication = this.dbnStatusList.find(x => x.value === 'For Adjudication')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (adjudication && adjudication.value)
    },
    assignedInvestigator () {
      const investigation = this.dbnStatusList.find(x => x.value === 'For Investigation')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (investigation && investigation.value)
    },
    assignedEnforcer () {
      const enforcement = this.dbnStatusList.find(x => x.value === 'For Enforcement')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (enforcement && enforcement.value)
    }
  },
  watch: {
    'formItem.exemptionToDSNotification' () {
      if (this.formItem.exemptionToDSNotification === 'No') {
        this.formItem.justificationForExemption = ''
        this.$v.formItem.justificationForExemption.$reset()
      }
    }
  },
  methods: {
    // if submit, trigger validations then show confirmation dialog. proceed to save when confirmed
    // if not submit, show confirmation dialog for save draft
    confirmDialog (submit) {
      if (submit) {
        this.$v.$touch()

        if (this.$v.$invalid) {
          return this.$store.dispatch('showAlert', {
            message: 'Cannot save FBNER with incomplete information.',
            type: 'error'
          })
        }
        this.$store.dispatch('showDialog', {
          title: 'Submit FBNER',
          titleColor: 'green',
          message: 'Are you sure you want to submit this FBNER for review?',
          buttonLabel: 'Submit',
          buttonColor: 'green',
          callback: async () => {
            this.$emit('loading', true)
            await this.save(true)
            this.$emit('loading', false)
          }
        })
      } else {
        this.$store.dispatch('showDialog', {
          title: 'Save as Draft',
          message: 'You can save the changes and update it later.',
          buttonLabel: 'Save',
          buttonColor: 'primary',
          callback: async () => {
            this.$emit('loading', true)
            await this.save(false)
            this.$emit('loading', false)
          }
        })
      }
    },
    // trigger validations. if valid, proceed to endorsing or next step in flow (depending on current user)
    approveFbner (process, user) {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return this.$store.dispatch('showAlert', {
          message: 'Cannot submit FBNER with incomplete info.',
          type: 'error'
        })
      }
      if (!(process === 'Enforcement' || process === 'Adjudication') || this.formItem.status !== 'Final Review - FBNER') {
        this.$store.dispatch('showDialog', {
          title: 'Submit FBNER',
          titleColor: 'green',
          message: `Are you sure you want to submit this FBNER for ${process}?`,
          buttonLabel: 'Submit',
          buttonColor: 'green',
          callback: async () => {
            this.$emit('loading', true)
            await this.updateFbner(process, user)
            this.$emit('loading', false)
          }
        })
      } else {
        this.$store.dispatch('showDialog', {
          title: 'Submit FBNER',
          titleColor: 'green',
          message: `Are you sure you want to endorse this FBNER to ${process}?`,
          buttonLabel: 'Submit',
          buttonColor: 'green',
          callback: async () => {
            this.$emit('loading', true)
            await this.endorseFbner(process, user)
            this.$emit('loading', false)
          }
        })
      }
    },
    // trigger validations for upload
    // if validations pass, prepare endorseTo, field, and fieldName according to proccess prop
    // use endorse API, show success or error message
    // reroute on success
    async endorseFbner (process, user) {
      this.$v.formItem.adjudicationFileUpload.$touch()
      this.$v.formItem.investigationFileUpload.$touch()
      this.$v.formItem.enforcementFileUpload.$touch()
      if (this.$v.formItem.adjudicationFileUpload.$invalid || this.$v.formItem.enforcementFileUpload.$invalid || this.$v.formItem.investigationFileUpload.$invalid) {
        return this.$store.dispatch('showAlert', {
          message: 'File upload is required.',
          type: 'error'
        })
      }
      this.$emit('loading', true)
      let endorseTo
      let field
      let fieldName
      switch (process) {
        case 'Enforcement':
          endorseTo = 'For Enforcement'
          field = this.formItem.adjudicationFileUpload
          fieldName = 'adjudicationFileUpload'
          break
        case 'Adjudication':
          endorseTo = 'For Adjudication'
          field = this.formItem.investigationFileUpload
          fieldName = 'investigationFileUpload'
          break
        case 'Investigation':
          endorseTo = 'For Investigation'
          break
        case 'Closing':
          endorseTo = 'Closed'
          field = this.formItem.enforcementFileUpload
          fieldName = 'enforcementFileUpload'
          break
        default:
          endorseTo = ''
          break
      }
      this.status = this.dbnStatusList.find(x => x.value === endorseTo)
      const payloadResp = await fbner.endorse(this.id, { status: this.status._id, endorserId: user })
      const payloadJson = await payloadResp.json()
      if (payloadResp.ok) {
        await this.fileUploader(this.id, field, fieldName)
        if (process !== 'Closing') {
          this.$store.dispatch('showAlert', {
            message: `You have successfully endorsed FBNER for ${this.formItem.dbnNumber} to ${process}.`,
            type: 'success'
          })
        } else {
          this.$store.dispatch('showAlert', {
            message: `You have successfully closed FBNER for ${this.formItem.dbnNumber}`,
            type: 'success'
          })
        }
        this.$router.push({
          name: 'DataBreachView',
          params: {
            dataTab: 'pdbnf',
            operation: 'view',
            id: this.formItem.pdbnf._id
          }
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: payloadJson.error,
          type: 'error'
        })
      }
      this.$emit('loading', false)
    },
    // function used to proceed to Initial Review, Final Review, or Endorsement (from Final Review)
    // use update API, show success or error message
    // reroute on success
    async updateFbner (process, user) {
      this.$emit('loading', true)
      this.isSubmitted = true
      let message = `You have successfully reviewed FBNER for ${this.formItem.dbnNumber}.`
      if (user) {
        this.formItem.endorserId = user
      }
      if (this.currentStatus.value === 'Initial Review - FBNER') {
        this.status = this.dbnStatusList.find(x => x.value === 'Final Review - FBNER')
      } else if (this.currentStatus.value === 'Final Review - FBNER') {
        let endorseTo = ''
        process === 'Adjudication' ? endorseTo = 'For Adjudication' : endorseTo = 'For Investigation'
        message = `You have successfully endorsed this FBNER for ${this.formItem.dbnNumber} for ${process}`
        this.status = this.dbnStatusList.find(x => x.value === endorseTo)
      }
      this.formItem.status = this.status._id
      const payloadResp = await this.fbnerCollection.update(this.id, this.schema)
      const payloadJson = await payloadResp.json()
      if (payloadResp.ok) {
        await this.changePdbnfStatus(this.formItem.pdbnf._id)
        this.$store.dispatch('showAlert', {
          message: message,
          type: 'success'
        })
        this.$router.push({
          name: 'DataBreachView',
          params: {
            dataTab: 'pdbnf',
            operation: 'view',
            id: this.formItem.pdbnf._id
          }
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: payloadJson.error,
          type: 'error'
        })
      }
      this.$emit('loading', false)
    },
    // function used for submitting and saving as draft
    // change pdbnf status when submitted
    // show success or error message. reroute on success
    async save (submit) {
      this.$emit('loading', true)
      let message = `You have successfully initiated FBNER for ${this.formItem.dbnNumber}!`
      if (submit) {
        this.isSubmitted = true
        this.isDraft = false
        if (this.$s.userRole.includes('evaluator')) {
          this.status = this.dbnStatusList.find(x => x.value === 'Initial Review - FBNER')
          this.$set(this.formItem, 'status', this.status._id)
          this.$set(this.formItem, 'evaluatedBy', this.assignedTo.evaluator._id)
        }
      } else {
        this.isSubmitted = false
        this.isDraft = true
        message = 'Your Final Breach Notification Evaluation Form was successfully saved as draft!'
        if (this.$s.userRole.includes('evaluator')) {
          this.status = this.dbnStatusList.find(x => x.value === 'Drafted')
        }
      }
      const payloadResp = (this.dbnID === this.id)
        ? await this.fbnerCollection.create(this.schema)
        : await this.fbnerCollection.update(this.id, this.schema)
      const payloadJson = await payloadResp.json()
      if (payloadResp.ok) {
        if (submit) await this.changePdbnfStatus(this.formItem.pdbnf._id ? this.formItem.pdbnf._id : this.formItem.pdbnf)
        this.$store.dispatch('showAlert', {
          message: message,
          type: this.isDraft ? 'draft' : 'success'
        })
        if (submit) {
          this.$router.push({
            name: 'DataBreachView',
            params: {
              dataTab: 'pdbnf',
              operation: 'view',
              id: this.formItem.pdbnf._id ? this.formItem.pdbnf._id : this.formItem.pdbnf
            }
          })
        } else {
          const id = this.formItem.pdbnf._id || this.formItem.pdbnf
          await this.getDrafts(id)
          this.$emit('emitDraft', this.isDraft, this.drafts)
          this.$router.push({
            name: 'FinalBreachEvaluationView',
            params:
            {
              role: 'evaluate',
              operation: 'view',
              id: payloadJson.entry && payloadJson.entry._id ? payloadJson.entry._id : payloadJson.updatedResult && payloadJson.updatedResult._id
            }
          })
        }
      } else {
        this.$store.dispatch('showAlert', {
          message: payloadJson.error,
          type: 'error'
        })
      }
      this.$emit('loading', false)
    },
    async changePdbnfStatus (dbn) {
      const resp = await crud('assign/pdbnf/statusUpdate').update(dbn, {
        status: this.status._id
      })
      return resp.ok
    }
  },
  validations () {
    return {
      formItem: custom.fbner(this)
    }
  }
}
</script>

<style scoped>
.cursor >>>  textarea{
  overflow-y: hidden !important;
  cursor: default !important
}
.chips-wrap >>> .v-size--default {
  height: auto !important;
}
.chips-wrap >>> .v-chip__content {
  white-space: normal !important;
}
</style>
